import type { PainPoint, PainPointStatus } from "@ti/core";
import { Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, CircleSlash, Eye, Lightbulb, Loader2, Quote, Radar, RotateCcw, Siren, Sparkles, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { PainKindBadge, PointsDelta } from "@/components/domain/analytics";
import { SERIES, Sparkline } from "@/components/domain/charts";
import { TimeAgo } from "@/components/domain/misc";
import { DeltaPct } from "@/components/domain/score";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/tooltip";
import { ROUTES, SessionLink, SessionsLink } from "@/features/analytics-shared/links";
import { useUpdatePainPoint } from "@/lib/api";
import { fmtInt, fmtMoney } from "@/lib/format";

const STATUS: Record<PainPointStatus, { label: string; tone: BadgeTone; icon: ReactNode }> = {
  new: { label: "New", tone: "accent", icon: <Sparkles /> },
  acknowledged: { label: "Acknowledged", tone: "info", icon: <Eye /> },
  tracking: { label: "Tracking", tone: "violet", icon: <Radar /> },
  resolved: { label: "Resolved", tone: "good", icon: <CheckCircle2 /> },
  dismissed: { label: "Dismissed", tone: "muted", icon: <CircleSlash /> },
};

export const PAIN_STATUSES = Object.keys(STATUS) as PainPointStatus[];
export const PAIN_STATUS_LABEL = Object.fromEntries(PAIN_STATUSES.map((s) => [s, STATUS[s].label])) as Record<PainPointStatus, string>;

export function PainStatusBadge({ status }: { status: PainPointStatus }) {
  const s = STATUS[status];
  return (
    <Badge tone={s.tone}>
      {s.icon}
      {s.label}
    </Badge>
  );
}

function Metric({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-fg-3">{label}</dt>
      <dd className="mt-0.5 text-[15px] font-semibold leading-tight tabular text-fg">{children}</dd>
      {hint ? <dd className="text-[11px] text-fg-3">{hint}</dd> : null}
    </div>
  );
}

export function PainPointCard({ p, rank, agentNames, useCaseNames }: { p: PainPoint; rank: number; agentNames: Map<string, string>; useCaseNames: Map<string, string> }) {
  const update = useUpdatePainPoint();
  const navigate = useNavigate();
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const open = p.status !== "resolved" && p.status !== "dismissed";
  const busy = (s: PainPointStatus) => update.isPending && update.variables?.status === s;
  const set = (status: Exclude<PainPointStatus, "new">, after?: () => void) => update.mutate({ id: p.id, status }, { onSuccess: after });

  return (
    <Card className="flex flex-col">
      <div className="flex flex-wrap items-start gap-3 border-b px-4 py-3">
        <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-md bg-surface-2 text-[11px] font-semibold tabular text-fg-2" aria-label={`Rank ${rank}`}>
          {rank}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-semibold text-fg">{p.title}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-3">
            <PainKindBadge kind={p.kind} />
            <PainStatusBadge status={p.status} />
            <span className="font-mono text-[11.5px] text-fg-2">{p.agentIds.map((a) => agentNames.get(a) ?? a).join(", ")}</span>
            {p.useCaseIds.map((u) => (
              <Link key={u} to={ROUTES.useCase(u)} className="hover:text-accent hover:underline">
                {useCaseNames.get(u) ?? u}
              </Link>
            ))}
            <span>
              · first seen <TimeAgo iso={p.firstSeen} className="text-fg-3" />
            </span>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {p.status === "new" ? (
            <Button size="sm" variant="ghost" onClick={() => set("acknowledged")} disabled={update.isPending}>
              {busy("acknowledged") ? <Loader2 className="animate-spin" /> : <Eye />} Acknowledge
            </Button>
          ) : null}
          {open && !p.signalDefinitionId ? (
            <Button size="sm" variant="outline" onClick={() => void navigate({ to: "/signals/studio/new", search: { fromPainPointId: p.id } })}>
              <Radar /> Track as signal
            </Button>
          ) : null}
          {open ? (
            <Button size="sm" variant="outline" onClick={() => set("resolved")} disabled={update.isPending}>
              {busy("resolved") ? <Loader2 className="animate-spin" /> : <CheckCircle2 />} Resolve
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => set("acknowledged")} disabled={update.isPending}>
              {busy("acknowledged") ? <Loader2 className="animate-spin" /> : <RotateCcw />} Reopen
            </Button>
          )}
          {p.status !== "dismissed" ? (
            <Button size="icon-sm" variant="ghost" onClick={() => setConfirmDismiss(true)} aria-label={`Dismiss “${p.title}”`} title="Dismiss">
              <X />
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-4 px-4 py-3 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-[13px] text-fg-2">{p.description}</p>
          {p.quotes.length ? (
            <ul className="flex flex-col gap-1.5" aria-label="What users said">
              {p.quotes.map((qt) => (
                <li key={qt.sessionId} className="flex items-start gap-2 rounded-md bg-surface-2 px-2.5 py-1.5">
                  <Quote className="mt-0.5 size-3.5 shrink-0 text-fg-3" aria-hidden />
                  <span className="min-w-0 flex-1 text-[13px] text-fg">{qt.text}</span>
                  <SessionLink id={qt.sessionId} className="shrink-0" />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-fg-3">No quotes: these sessions were collected without content (metadata-only privacy mode).</p>
          )}
          <div className="flex gap-2 rounded-md border border-dashed px-2.5 py-2 text-xs text-fg-2">
            <Lightbulb className="mt-px size-3.5 shrink-0 text-fg-3" aria-hidden />
            <span>
              <span className="font-medium text-fg">Suggestion:</span> {p.suggestion}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-3">
            <Metric label="Sessions · 7d" hint={<DeltaPct value={p.deltaPct} suffix=" vs prior 7d" invert />}>
              {fmtInt(p.sessions7d)}
            </Metric>
            <Metric label="Affected users">{fmtInt(p.affectedUsers7d)}</Metric>
            <Metric label="Success impact" hint="vs comparable sessions">
              <Tooltip content="Success rate of affected sessions minus that of comparable unaffected ones">
                <span tabIndex={0}>
                  <PointsDelta value={p.successImpact} className="text-[15px] font-semibold" />
                </span>
              </Tooltip>
            </Metric>
            <Metric label="Cost impact · 7d" hint="extra turns, escalations, lost value">
              {fmtMoney(p.costImpact7d, { compact: true })}
            </Metric>
          </dl>
          <div>
            <div className="mb-1 text-[11px] text-fg-3">Daily sessions, 14 days</div>
            <Sparkline data={p.trend} width={280} height={36} color={SERIES[0]} label={`Daily affected sessions over 14 days, from ${p.trend[0]} to ${p.trend[p.trend.length - 1]}`} />
          </div>
        </div>
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t px-4 py-2 text-xs">
        <SessionsLink filter={{ painPointId: p.id }}>View {fmtInt(p.sessions7d)} sessions</SessionsLink>
        {p.signalDefinitionId ? (
          <Link to="/signals/studio/$definitionId" params={{ definitionId: p.signalDefinitionId }} className="inline-flex items-center gap-1 text-fg-2 hover:text-accent hover:underline">
            <Radar className="size-3.5 text-fg-3" aria-hidden /> Tracked by a signal
          </Link>
        ) : null}
        {p.issueId ? (
          <Link to="/issues/$issueId" params={{ issueId: p.issueId }} className="inline-flex items-center gap-1 text-fg-2 hover:text-accent hover:underline">
            <Siren className="size-3.5 text-fg-3" aria-hidden /> <span className="font-mono text-[11.5px]">{p.issueId}</span>
          </Link>
        ) : null}
      </div>

      <Dialog open={confirmDismiss} onOpenChange={setConfirmDismiss}>
        <DialogContent title={`Dismiss “${p.title}”?`} description="It leaves the ranked list and discovery stops surfacing it. You can find it under the Dismissed filter and reopen it.">
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" disabled={update.isPending} onClick={() => set("dismissed", () => setConfirmDismiss(false))}>
              {busy("dismissed") ? <Loader2 className="animate-spin" /> : <X />} Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
